import camelcaseKeys from 'camelcase-keys';
import ContactAPI from 'dashboard/api/contacts';
import { isPhoneE164 } from 'shared/helpers/Validators';

// Standard 3x4 dialpad layout.
export const DIALPAD_KEYS = [
  '1',
  '2',
  '3',
  '4',
  '5',
  '6',
  '7',
  '8',
  '9',
  '*',
  '0',
  '#',
];

/**
 * Strips spaces/dashes/parens and ensures the number carries a leading '+',
 * auto-prepending one when the agent starts typing digits directly.
 * @param {string} value
 * @returns {string}
 */
export const normalizePhoneNumber = value => {
  const stripped = (value || '').replace(/[\s\-()]/g, '');
  if (!stripped) return '';
  return stripped.startsWith('+') ? stripped : `+${stripped}`;
};

/**
 * @param {string} value
 * @returns {boolean} True when the normalized number is a valid E.164 number.
 */
export const isValidDialpadNumber = value =>
  isPhoneE164(normalizePhoneNumber(value));

/**
 * @param {string} value - Current raw dialpad input.
 * @param {string} digit - Digit/symbol pressed on the dialpad grid.
 * @returns {string}
 */
export const appendDigit = (value, digit) => `${value || ''}${digit}`;

/**
 * @param {string} value - Current raw dialpad input.
 * @returns {string} The value with its last character removed.
 */
export const backspaceDigit = value => (value || '').slice(0, -1);

/**
 * The contacts search endpoint doesn't support exact-match filtering by
 * phone number, so this searches and then confirms an exact match client-side.
 * @param {string} phoneNumber - Normalized E.164 phone number.
 * @returns {Promise<Object|null>} The matching contact, or null if none found.
 */
export const findContactByPhone = async phoneNumber => {
  const {
    data: { payload },
  } = await ContactAPI.search(phoneNumber, 1, 'name', '');

  const contacts = camelcaseKeys(payload || [], { deep: true });
  return contacts.find(contact => contact.phoneNumber === phoneNumber) || null;
};

/**
 * @param {{ name: string, phoneNumber: string }} params
 * @returns {Promise<Object>} The newly created contact.
 */
export const createContact = async ({ name, phoneNumber }) => {
  const {
    data: {
      payload: { contact },
    },
  } = await ContactAPI.create({ name, phone_number: phoneNumber });

  return camelcaseKeys(contact, { deep: true });
};
